import dayjs, {
  addDaysToPlainDate,
  todayInManila,
} from "../../../utils/dateTime";
import { getApiErrorMessage } from "../../../utils/apiError";

export const EVALUATION_STATUS = {
  PENDING_SELF: { label: "Self-assessment", tone: "gray" },
  PENDING_EVALUATION: { label: "For rating", tone: "amber" },
  PENDING_ACKNOWLEDGEMENT: { label: "Waiting for employee", tone: "purple" },
  CONTESTED: { label: "Contested – HR to resolve", tone: "amber" },
  COMPLETED: { label: "Completed", tone: "green" },
  CANCELLED: { label: "Cancelled", tone: "red" },
};

/**
 * Status label; a completed evaluation also says how it ended
 * ("Completed · Agreed" or "Completed · Contest upheld").
 */
export const statusLabel = (ev) => {
  const meta = EVALUATION_STATUS[ev.status];
  if (!meta) return ev.status || "—";
  if (ev.status === "COMPLETED" && ev.employeeResponse) {
    return `${meta.label} · ${ev.employeeResponse === "CONTEST" ? "Contest upheld" : "Agreed"}`;
  }
  return meta.label;
};

const CLOSED = ["COMPLETED", "CANCELLED"];
export const isOpen = (ev) => !CLOSED.includes(ev.status);

/** Past its due date and still open (computed client-side; the API has no overdue filter). */
export const isOverdue = (ev) =>
  !!ev.dueDate && isOpen(ev) && ev.dueDate < todayInManila();

/** Template validation / response validation errors come back as an array. */
export const errorList = (err, fallback) => {
  const msg = err?.response?.data?.message;
  return Array.isArray(msg) ? msg : [getApiErrorMessage(err, fallback)];
};

/** Most recent send-back (HR re-evaluation, or an old sign-off return): why it's back with the evaluator. */
export const latestReturn = (activities = []) =>
  [...activities]
    .reverse()
    .find(
      (a) => a.action === "CONTEST_REEVALUATE" || a.action === "RETURNED",
    ) || null;

export const ACTIVITY_META = {
  CREATED: { label: "Created", icon: "bi-plus-circle", tone: "gray" },
  SELF_SUBMITTED: {
    label: "Self-assessment submitted",
    icon: "bi-person-check",
    tone: "blue",
  },
  SUBMITTED: {
    label: "Submitted to the employee",
    icon: "bi-send",
    tone: "blue",
  },
  ACKNOWLEDGED: {
    label: "Acknowledged by employee",
    icon: "bi-hand-thumbs-up",
    tone: "green",
  },
  EVALUATOR_CHANGED: {
    label: "Evaluator changed",
    icon: "bi-arrow-left-right",
    tone: "amber",
  },
  CANCELLED: { label: "Cancelled", icon: "bi-x-circle", tone: "red" },
  ACCEPTED: {
    label: "Accepted by employee",
    icon: "bi-hand-thumbs-up",
    tone: "green",
  },
  CONTESTED: {
    label: "Contested by employee",
    icon: "bi-exclamation-octagon",
    tone: "amber",
  },
  CONTEST_UPHELD: {
    label: "Contest resolved: result upheld",
    icon: "bi-check2-square",
    tone: "green",
  },
  CONTEST_REEVALUATE: {
    label: "Contest resolved: re-evaluation",
    icon: "bi-arrow-repeat",
    tone: "amber",
  },
  // Older entries (before sign-off was removed)
  APPROVED: {
    label: "Approved (old sign-off)",
    icon: "bi-check-circle",
    tone: "gray",
  },
  RETURNED: {
    label: "Returned (old sign-off)",
    icon: "bi-arrow-return-left",
    tone: "gray",
  },
};

/* ---------- Period presets for the create forms ---------- */
const quarter = (year, q) => {
  const startMonth = (q - 1) * 3 + 1;
  const start = dayjs(
    `${year}-${String(startMonth).padStart(2, "0")}-01`,
    "YYYY-MM-DD",
    true,
  );
  return {
    start: start.format("YYYY-MM-DD"),
    end: start.add(3, "month").subtract(1, "day").format("YYYY-MM-DD"),
  };
};

/**
 * Presets: last year's / this year's annual, Q1–Q4 of this year, probationary
 * (hiring date + 6 months; single create only), custom.
 */
export const periodPresets = (thisYear, { withProbationary } = {}) => [
  {
    key: `annual-${thisYear - 1}`,
    label: `${thisYear - 1} Annual`,
    start: `${thisYear - 1}-01-01`,
    end: `${thisYear - 1}-12-31`,
  },
  {
    key: `annual-${thisYear}`,
    label: `${thisYear} Annual`,
    start: `${thisYear}-01-01`,
    end: `${thisYear}-12-31`,
  },
  ...[1, 2, 3, 4].map((q) => ({
    key: `q${q}-${thisYear}`,
    label: `Q${q} ${thisYear}`,
    ...quarter(thisYear, q),
  })),
  ...(withProbationary
    ? [{ key: "probationary", label: "Probationary", probationary: true }]
    : []),
  { key: "custom", label: "Custom" },
];

/** Probationary period from a plain hiring date: hire date → 6 months later (minus a day). */
export const probationaryPeriod = (hiringDate) => {
  const d = hiringDate
    ? dayjs(String(hiringDate).slice(0, 10), "YYYY-MM-DD", true)
    : null;
  if (!d || !d.isValid()) return { start: "", end: "" };
  return {
    start: d.format("YYYY-MM-DD"),
    end: d.add(6, "month").subtract(1, "day").format("YYYY-MM-DD"),
  };
};

/** Default due date: period end + 15 days. */
export const defaultDueDate = (periodEnd) =>
  periodEnd ? addDaysToPlainDate(periodEnd, 15) : "";

/** Validation shared by single and bulk create (the server checks again). */
export const periodProblems = ({ periodLabel, periodStart, periodEnd }) => {
  const list = [];
  if (!periodLabel?.trim()) list.push("Enter a period label.");
  if (!periodStart || !periodEnd) list.push("Pick the period start and end.");
  else if (periodEnd < periodStart)
    list.push("Period end must be on or after the start.");
  return list;
};
